import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { loadGoogleMaps } from '../../helper/googleMapsLoader';
import config from '../../config';

const ControlTowerMap = ({ venues }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const placesServiceRef = useRef(null); // Ref for PlacesService
    const activeInfoWindowRef = useRef(null); // Track active InfoWindow
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [apiError, setApiError] = useState(null);

    // UAE Coordinates (Abu Dhabi roughly center)
    const DEFAULT_CENTER = { lat: 24.0, lng: 54.0 };
    const DEFAULT_ZOOM = 7;

    // Helper function for smooth zooming
    const smoothZoom = (map, targetZoom, currentZoom) => {
        if (currentZoom >= targetZoom) {
            return;
        }
        
        const z = window.google.maps.event.addListener(map, 'zoom_changed', () => {
            window.google.maps.event.removeListener(z);
            smoothZoom(map, targetZoom, currentZoom + 1);
        });
        
        setTimeout(() => {
            map.setZoom(currentZoom);
        }, 80); // 80ms delay between zoom steps for smooth animation
    };

    useEffect(() => {
        let cancelled = false;
        
        // Global handler for auth errors
        window.gm_authFailure = () => {
            if (!cancelled) {
                console.error('Google Maps authentication failed');
                setApiError('Google Maps authentication failed. Please check your API key and restrictions.');
            }
        };

        // Dynamically load Google Maps API if not already loaded
        loadGoogleMaps()
            .then(() => {
                if (!cancelled) {
                    setIsApiLoaded(true);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Google Maps load error:', err);
                    setApiError(err.message);
                }
            });
            
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        // Wait for API to be loaded
        if (!isApiLoaded || !window.google?.maps) {
            return;
        }

        try {
            // Initialize map if not already done
            if (!mapInstanceRef.current && mapRef.current) {
                mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                    center: DEFAULT_CENTER,
                    zoom: DEFAULT_ZOOM,
                    mapId: "ba2965b691db326f85999d61", // HubScroll Web App Map ID
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                    clickableIcons: true, // Explicitly enable clicking on POIs (Restaurants, etc.)
                });

                // Initialize PlacesService
                placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current);

                // Global click listener on map to close active InfoWindow
                mapInstanceRef.current.addListener('click', (e) => {
                    // If user clicks a POI, the default behavior handles it.
                    // But we want to show photos if available, so we intercept.
                    if (e.placeId) {
                         e.stop(); // Stop default small window

                         if (activeInfoWindowRef.current) {
                             activeInfoWindowRef.current.close();
                         }

                         const request = {
                             placeId: e.placeId,
                             fields: ['name', 'formatted_address', 'photos', 'geometry', 'rating', 'user_ratings_total', 'url']
                         };

                         placesServiceRef.current.getDetails(request, (place, status) => {
                             if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                                 let photosHtml = '';
                                 if (place.photos && place.photos.length > 0) {
                                     // Get the first photo URL (max width 400px)
                                     const photoUrl = place.photos[0].getUrl({ maxWidth: 400, maxHeight: 200 });
                                     photosHtml = `<img src="${photoUrl}" alt="${place.name}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;" />`;
                                 }

                                 const content = `
                                     <div style="padding: 4px; min-width: 220px; max-width: 260px;">
                                         ${photosHtml}
                                         <h6 style="margin: 0 0 4px 0; font-weight: 600; font-size: 14px;">${place.name}</h6>
                                         <p style="margin: 0 0 6px 0; font-size: 12px; color: #666;">${place.formatted_address}</p>
                                         <div style="display: flex; align-items: center; justify-content: space-between;">
                                             <div style="font-size: 12px; color: #f59e0b; font-weight: 600;">
                                                 ${place.rating ? `★ ${place.rating} (${place.user_ratings_total})` : ''}
                                             </div>
                                             ${place.url ? `<a href="${place.url}" target="_blank" style="font-size: 11px; color: #0d6efd; text-decoration: none;">View on Maps →</a>` : ''}
                                         </div>
                                     </div>
                                 `;

                                 const infoWindow = new window.google.maps.InfoWindow({
                                     content: content,
                                     position: e.latLng // Use the clicked position
                                 });

                                 infoWindow.open(mapInstanceRef.current);
                                 activeInfoWindowRef.current = infoWindow;
                             }
                         });
                         return;
                    }

                    if (activeInfoWindowRef.current) {
                        activeInfoWindowRef.current.close();
                        activeInfoWindowRef.current = null;
                    }
                });
            }

            // Update markers
            if (mapInstanceRef.current && venues) {
                // Clear existing markers
                markersRef.current.forEach(marker => marker.map = null);
                markersRef.current = [];

                const bounds = new window.google.maps.LatLngBounds();
                let hasValidVenue = false;
                const useAdvancedMarkers = window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement;

                if (!useAdvancedMarkers) {
                    console.warn('AdvancedMarkerElement not available, falling back to legacy Marker');
                }

                venues.forEach(venue => {
                    let position = null;
                    
                    // Parse location if it's a string (JSON) or use directly if object
                    if (venue.location) {
                        try {
                            const loc = typeof venue.location === 'string' 
                                ? JSON.parse(venue.location) 
                                : venue.location;
                                
                            if (loc && loc.coordinates && Array.isArray(loc.coordinates)) {
                                // GeoJSON format: [lng, lat]
                                position = { 
                                    lat: parseFloat(loc.coordinates[1]), 
                                    lng: parseFloat(loc.coordinates[0]) 
                                };
                            } else if (loc && loc.lat && loc.lng) {
                                // Direct lat/lng object
                                position = { 
                                    lat: parseFloat(loc.lat), 
                                    lng: parseFloat(loc.lng) 
                                };
                            }
                        } catch (e) {
                            console.warn('Failed to parse location for venue:', venue.id, e);
                        }
                    }
                    // Fallback to direct latitude/longitude fields if available
                    else if (venue.latitude && venue.longitude) {
                        position = {
                            lat: parseFloat(venue.latitude),
                            lng: parseFloat(venue.longitude)
                        };
                    }

                    if (position) {
                        hasValidVenue = true;
                        bounds.extend(position);

                        // Prepare Thumbnail HTML FIRST
                        // Assuming venue.thumbnail or venue.logo contains the URL
                        // Handle both relative paths (from API) and full URLs (if any)
                        const rawImageUrl = venue.thumbnail || venue.logo;
                        let imageUrl = null;
                        
                        if (rawImageUrl) {
                            if (rawImageUrl.startsWith('http')) {
                                imageUrl = rawImageUrl;
                            } else {
                                // Remove leading slash if present to avoid double slash
                                const cleanPath = rawImageUrl.startsWith('/') ? rawImageUrl.substring(1) : rawImageUrl;
                                imageUrl = `${config.API_BASE_URL}/${cleanPath}`;
                            }
                        }

                        const imageHtml = imageUrl 
                            ? `<img src="${imageUrl}" alt="${venue.name}" style="width: 100%; height: 100px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;" onError="this.style.display='none'"/>`
                            : '';

                        // Create InfoWindow content with image
                        const infoWindowContent = `
                            <div style="padding: 4px; min-width: 200px; max-width: 220px;">
                                ${imageHtml}
                                <h6 style="margin: 0 0 4px 0; font-weight: 600; font-size: 14px; line-height: 1.2;">
                                    ${venue.name || venue.business_name || venue.title || 'Unknown Venue'}
                                </h6>
                                <p style="margin: 0 0 6px 0; font-size: 12px; color: #666;">
                                    ${venue.address?.city || venue.city || 'No city'}
                                </p>
                                <div style="font-size: 11px;">
                                    ${venue.is_verified 
                                        ? '<span style="color: #198754; font-weight: 600;">✓ Verified</span>' 
                                        : '<span style="color: #ffc107; font-weight: 600;">⚠ Pending</span>'}
                                </div>
                            </div>
                        `;

                        const infoWindow = new window.google.maps.InfoWindow({
                            content: infoWindowContent
                        });

                        let marker;
                        
                        if (useAdvancedMarkers) {
                            const pinElement = new window.google.maps.marker.PinElement({
                                scale: 0.8,
                                background: '#dc3545',
                                borderColor: '#b02a37',
                                glyphColor: 'white',
                            });

                            marker = new window.google.maps.marker.AdvancedMarkerElement({
                                position,
                                map: mapInstanceRef.current,
                                title: venue.name || venue.business_name || venue.title,
                                content: pinElement.element
                            });

                            // Use gmp-click for AdvancedMarkerElement
                            marker.addListener('gmp-click', () => {
                                // Close previously active InfoWindow
                                if (activeInfoWindowRef.current) {
                                    activeInfoWindowRef.current.close();
                                }

                                // Open new InfoWindow
                                infoWindow.open(mapInstanceRef.current, marker);
                                activeInfoWindowRef.current = infoWindow;

                                // Zoom and Pan behavior
                                mapInstanceRef.current.panTo(position);
                                mapInstanceRef.current.setZoom(17);
                            });
                        } else {
                            marker = new window.google.maps.Marker({
                                position,
                                map: mapInstanceRef.current,
                                title: venue.name || venue.business_name || venue.title
                            });

                            // Use standard click for legacy Marker
                            marker.addListener('click', () => {
                                // Close previously active InfoWindow
                                if (activeInfoWindowRef.current) {
                                    activeInfoWindowRef.current.close();
                                }

                                // Open new InfoWindow
                                infoWindow.open(mapInstanceRef.current, marker);
                                activeInfoWindowRef.current = infoWindow;

                                // Zoom and Pan behavior
                                mapInstanceRef.current.panTo(position);
                                mapInstanceRef.current.setZoom(17);
                            });
                        }

                        markersRef.current.push(marker);
                    }
                });

                // Fit bounds if we have markers (only on initial load or significant venue change)
                // We use a simple heuristic: if markers changed significantly, refit. 
                // For now, we fit bounds every time venues update, but the user can then zoom freely.
                if (hasValidVenue && markersRef.current.length > 0) {
                     // Only fit bounds if we are not already zoomed in on a specific venue (optional UX choice)
                     // But for "Live Map" usually you want to see all unless interacted with.
                     // To avoid resetting zoom when just clicking a marker (which shouldn't trigger this effect hook unless venues prop changes), this is safe.
                    mapInstanceRef.current.fitBounds(bounds);
                    
                    const listener = window.google.maps.event.addListener(mapInstanceRef.current, "idle", () => { 
                        if (mapInstanceRef.current.getZoom() > 15) mapInstanceRef.current.setZoom(15); 
                        window.google.maps.event.removeListener(listener); 
                    });
                } else {
                    mapInstanceRef.current.setCenter(DEFAULT_CENTER);
                    mapInstanceRef.current.setZoom(DEFAULT_ZOOM);
                }
            }
        } catch (error) {
            console.error('Error initializing map:', error);
            setApiError(`Map initialization failed: ${error.message}`);
        }
    }, [isApiLoaded, venues]);

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">Live Venue Map (UAE)</h6>
            </div>
            <div className="card-body p-0" style={{ height: '400px' }}>
                <div ref={mapRef} style={{ height: '100%', width: '100%' }}>
                    {apiError && (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light">
                            <Icon icon="mdi:alert-circle" className="text-4xl text-danger mb-12" />
                            <p className="text-danger">{apiError}</p>
                        </div>
                    )}
                    {!isApiLoaded && !apiError && (
                        <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light">
                            <div className="spinner-border text-primary mb-12" role="status">
                                <span className="visually-hidden">Loading Google Maps...</span>
                            </div>
                            <p className="text-secondary-light">Loading Google Maps...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ControlTowerMap;
